import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Zap as ZapIcon } from 'lucide-react';

const PRESETS = {
  'zero-click':   { tags: ['Direct Flow', 'No Creatives', 'Fast Launch'],          accent: '#22D3EE' },
  'domain-redirect': { tags: ['Intent-Driven', 'Vertical-Aware', 'Compatible Trackers'], accent: '#3B82F6' },
  'pop':          { tags: ['High Volume', 'Cost-Efficient', 'Lander Testing'],     accent: '#8B5CF6' },
  'push':         { tags: ['Engaging', 'Creative-Led', 'Mobile Friendly'],         accent: '#34D399' },
  'in-page-push': { tags: ['iOS Friendly', 'Native-Like', 'Browser-Wide'],         accent: '#22D3EE' },
  'direct-click': { tags: ['Smart Routing', 'Performance Focused', 'Quick to Test'], accent: '#FBBF24' },
};

export default function FormatCard({ format }) {
  const Icon = format.icon;
  const cfg = PRESETS[format.slug] || { tags: [], accent: '#22D3EE' };
  const accent = cfg.accent;
  return (
    <Link
      to={`/ad-formats/${format.slug}`}
      data-testid="traffic-format-card"
      className="group relative rounded-2xl border border-white/10 p-6 backdrop-blur-xl lift-on-hover overflow-hidden"
      style={{
        background:
          'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = `${accent}55`;
        e.currentTarget.style.boxShadow = `0 24px 70px ${accent}22, 0 0 0 1px ${accent}22 inset`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '';
        e.currentTarget.style.boxShadow = '';
      }}
    >
      {/* Top gradient accent */}
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
        aria-hidden
      />
      {/* Hover halo */}
      <div
        className="absolute -top-16 -right-16 h-44 w-44 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ background: `radial-gradient(circle, ${accent}33, transparent 65%)` }}
        aria-hidden
      />

      {/* Animated signal line */}
      <svg className="absolute top-3 right-3 opacity-65 group-hover:opacity-100 transition-opacity" width="64" height="22" viewBox="0 0 64 22" aria-hidden>
        <defs>
          <linearGradient id={`sig-${format.slug}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={accent} stopOpacity="0" />
            <stop offset="50%" stopColor={accent} />
            <stop offset="100%" stopColor={accent} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d="M 0 16 L 8 16 L 12 6 L 18 18 L 24 4 L 30 14 L 36 8 L 44 16 L 64 16"
          fill="none"
          stroke={`url(#sig-${format.slug})`}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="64" cy="16" r="2.2" fill={accent} className="pulse-glow" style={{ filter: `drop-shadow(0 0 6px ${accent})` }} />
      </svg>

      <div className="flex items-start justify-between">
        <span
          className="relative inline-flex h-11 w-11 items-center justify-center rounded-xl border"
          style={{
            background: `linear-gradient(135deg, ${accent}28, ${accent}10)`,
            borderColor: `${accent}40`,
            color: accent,
            boxShadow: `0 0 22px ${accent}33`,
          }}
        >
          <Icon className="h-5 w-5" />
          <span className="absolute -inset-1.5 rounded-2xl blur-lg opacity-30 -z-10" style={{ background: accent }} aria-hidden />
        </span>
      </div>

      <h3 className="font-display mt-5 text-[20px] font-semibold tracking-[-0.025em] text-white">{format.title}</h3>
      <p className="mt-2 text-[14.5px] text-slate-300/85 leading-relaxed line-clamp-2">{format.short}</p>

      <div className="mt-5 flex flex-wrap gap-1.5">
        {cfg.tags.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/[0.04] border text-[10.5px] font-medium"
            style={{ color: accent, borderColor: `${accent}40` }}
          >
            <ZapIcon className="h-2.5 w-2.5" /> {t}
          </span>
        ))}
      </div>
      <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between text-[12px]">
        <span className="text-slate-500"><span className="text-slate-300">Best for:</span> {format.bestFor}</span>
        <span
          className="inline-flex items-center gap-1 text-[12px] font-semibold transition-transform duration-200 group-hover:translate-x-0.5"
          style={{ color: accent }}
          data-testid="traffic-format-card-cta"
        >
          View Format <ArrowUpRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </Link>
  );
}
